<template>
  <div class="space-y-6">
    <h2 class="text-3xl font-bold text-gray-900">Shop Test</h2>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Left Column: Test Configuration -->
      <div class="space-y-6">
        <div class="card">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Authentication</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">x-shop-api-key</label>
              <input v-model="shopApiKey" @input="onKeyChanged" type="password" placeholder="enter your shop api key once" class="form-input" />
              <p class="text-xs text-gray-500 mt-1">Saved locally in your browser (localStorage). Function URL is read from VITE_FUNCTION_URL.</p>
            </div>
          </div>
        </div>

        <div class="card">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Latest SKU Lookup</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Base plan</label>
              <select v-model="latestBase" class="form-select">
                <option v-for="bp in basePlans" :key="'lb-'+bp" :value="bp">{{ bp }}</option>
              </select>
            </div>
            <div class="flex items-end gap-3">
              <button class="btn btn-primary" @click.prevent="lookupLatest" :disabled="loadingLatest">{{ loadingLatest ? 'Looking up...' : 'Get latest SKU' }}</button>
            </div>
          </div>
        </div>


        <div class="card">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Shop Provision</h3>
          <form @submit.prevent="provision" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                <select v-model="plan" class="form-select">
                  <option v-for="p in skuPlans" :key="p" :value="p">{{ p }}</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Subscription Id</label>
                <input v-model="subscriptionId" type="text" placeholder="sub-123" class="form-input" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Expires At (UTC) - Required</label>
                <input v-model="expiresAt" type="text" placeholder="2026-01-31T00:00:00Z" class="form-input" required />
              </div>
            </div>
            <div>
              <button type="submit" class="btn btn-primary" :disabled="loadingProvision">{{ loadingProvision ? 'Provisioning...' : 'Provision' }}</button>
            </div>
          </form>
        </div>

        <div class="card">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Shop Upgrade</h3>
          <form @submit.prevent="upgrade" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">New SKU</label>
                <select v-model="newPlan" class="form-select">
                  <option v-for="p in skuPlans" :key="'new-'+p" :value="p">{{ p }}</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Subscription Id</label>
                <input v-model="subscriptionId" type="text" placeholder="sub-123" class="form-input" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Expires At (UTC) - Required</label>
                <input v-model="upgradeExpiresAt" type="text" placeholder="2026-01-31T00:00:00Z" class="form-input" required />
              </div>
            </div>
            <div>
              <button type="submit" class="btn btn-primary" :disabled="loadingUpgrade">{{ loadingUpgrade ? 'Upgrading...' : 'Upgrade' }}</button>
            </div>
          </form>
        </div>

        <div class="card">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Shop Renew</h3>
          <form @submit.prevent="renew" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Subscription Id</label>
                <input v-model="subscriptionId" type="text" placeholder="sub-123" class="form-input" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Expires At (UTC)</label>
                <input v-model="renewExpiresAt" type="text" placeholder="2026-01-31T00:00:00Z" class="form-input" />
              </div>
            </div>
            <div>
              <button type="submit" class="btn btn-primary" :disabled="loadingRenew">{{ loadingRenew ? 'Renewing...' : 'Renew' }}</button>
            </div>
          </form>
        </div>

        <div class="card">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Shop Cancel</h3>
          <form @submit.prevent="cancelSub" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Subscription Id</label>
                <input v-model="subscriptionId" type="text" placeholder="sub-123" class="form-input" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Cancel At (UTC)</label>
                <input v-model="cancelAt" type="text" placeholder="2026-01-31T00:00:00Z" class="form-input" />
              </div>
            </div>
            <div>
              <button type="submit" class="btn btn-primary" :disabled="loadingCancel">{{ loadingCancel ? 'Canceling...' : 'Cancel' }}</button>
            </div>
          </form>
        </div>

        <div class="card">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Shop Rotate</h3>
          <form @submit.prevent="rotate" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Subscription Id</label>
                <input v-model="subscriptionId" type="text" placeholder="sub-123" class="form-input" />
              </div>
            </div>
            <div>
              <button type="submit" class="btn btn-primary" :disabled="loadingRotate">{{ loadingRotate ? 'Rotating...' : 'Rotate' }}</button>
            </div>
          </form>
        </div>

        <div class="card">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Usage</h3>
          <form @submit.prevent="getUsage" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Subscription Id</label>
              <input v-model="subscriptionId" type="text" placeholder="sub-123" class="form-input" />
            </div>
            <div>
              <button type="submit" class="btn btn-primary" :disabled="loadingUsage">{{ loadingUsage ? 'Fetching...' : 'Get Usage' }}</button>
            </div>
          </form>
        </div>
      </div>

      <!-- Right Column: Response -->
      <div class="space-y-6">
        <!-- Live Plans from Azure Storage -->
        <div class="card">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Azure Storage Plans (live)</h3>
          <div v-if="basePlans.length === 0" class="text-sm text-gray-500">No plans found.</div>
          <div v-else class="space-y-3">
            <div>
              <div class="text-sm font-medium text-gray-900 mb-1">Base plans</div>
              <div class="flex flex-wrap gap-2">
                <span v-for="bp in basePlans" :key="'bp-'+bp" class="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">{{ bp }}</span>
              </div>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full text-sm">
                <thead>
                  <tr class="text-left text-gray-600">
                    <th class="py-1 pr-4">Name</th>
                    <th class="py-1 pr-4">Minute</th>
                    <th class="py-1 pr-4">Day</th>
                    <th class="py-1 pr-4">Month</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sp in storagePlans" :key="sp.name" class="border-t border-gray-100">
                    <td class="py-1 pr-4">{{ sp.name }}</td>
                    <td class="py-1 pr-4">{{ sp.minuteLimit }}</td>
                    <td class="py-1 pr-4">{{ sp.dayLimit }}</td>
                    <td class="py-1 pr-4">{{ sp.monthLimit }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-if="response" class="card">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Response</h3>
          <div class="space-y-2 text-sm">
            <div><span class="font-medium">Status:</span> <span :class="response.success ? 'text-green-600' : 'text-red-600'">{{ response.status }} {{ response.statusText }}</span></div>
            <div><span class="font-medium">Time:</span> {{ response.elapsedMs }}ms</div>
          </div>
          <div class="mt-4">
            <h4 class="text-sm font-semibold text-gray-900 mb-2">Sent Request</h4>
            <pre class="bg-gray-50 rounded-md p-3 text-xs whitespace-pre-wrap">{{ sentRequest }}</pre>
          </div>
          <div class="mt-4">
            <h4 class="text-sm font-semibold text-gray-900 mb-2">Response Body</h4>
            <pre class="bg-gray-50 rounded-md p-3 text-xs whitespace-pre-wrap">{{ formatJson(response.body) }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { adminApi } from '@/services/api'

const functionUrl = ref(import.meta.env.VITE_FUNCTION_URL || '/');
// Prefill with known dev/test key for convenience
const shopApiKey = ref(localStorage.getItem('shopApiKey') ?? '');
const storagePlans = ref<any[]>([]);
const basePlans = ref<string[]>([]); // only for display chips
const skuPlans = ref<string[]>([]);  // full SKUs for dropdowns
const plan = ref('');
const newPlan = ref('');
const subscriptionId = ref('test-sub-id-911');
const upgradeExpiresAt = ref('');
const expiresAt = ref('');
const renewExpiresAt = ref('');
const cancelAt = ref('');
// Latest SKU test inputs
const latestBase = ref('');
const latestSku = ref<string | null>(null);
const loadingLatest = ref(false);
const loadingProvision = ref(false);
const loadingUpgrade = ref(false);
const loadingRenew = ref(false);
const loadingCancel = ref(false);
const loadingUsage = ref(false);
const loadingRotate = ref(false);

const response = ref<{ success: boolean; status: number; statusText: string; elapsedMs: number; body: any } | null>(null);
const sentRequest = ref('');

const formatJson = (obj: any) => {
  try { return JSON.stringify(typeof obj === 'string' ? JSON.parse(obj) : obj, null, 2); } catch { return String(obj); }
};

function isoOneYearFromNow(): string {
  const d = new Date();
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  d.setUTCSeconds(0, 0);
  // Produce an ISO string without milliseconds, e.g., 2026-12-31T00:00:00Z
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

onMounted(async () => {
  const def = isoOneYearFromNow();
  expiresAt.value = def;
  upgradeExpiresAt.value = def;
  renewExpiresAt.value = def;
  // initialize from localStorage if present
  const saved = localStorage.getItem('shopApiKey');
  if (saved && !shopApiKey.value) shopApiKey.value = saved;

  await loadStoragePlans();
});


async function loadStoragePlans() {
  try {
    const items = await adminApi.getStoragePlans();
    storagePlans.value = Array.isArray(items) ? items : [];
    // Sort by name for stable display
    storagePlans.value.sort((a: any, b: any) => String(a?.name ?? '').localeCompare(String(b?.name ?? '')));

    // Build base plan list for display chips
    const bases = new Set<string>();
    // Build SKU list for dropdowns
    const skus: string[] = [];
    for (const p of storagePlans.value) {
      const name = String(p?.name ?? '');
      if (name) {
        skus.push(name);
        const base = name.replace(/\.v\d+$/, '');
        if (base) bases.add(base);
      }
    }
    basePlans.value = Array.from(bases).sort();
    skuPlans.value = skus.sort();

    if (!plan.value && skuPlans.value.length) plan.value = skuPlans.value[0];
    if (!newPlan.value && skuPlans.value.length) newPlan.value = skuPlans.value[0];
    if (!latestBase.value && basePlans.value.length) latestBase.value = basePlans.value[0];
  } catch (e) {
    storagePlans.value = [];
    basePlans.value = [];
    skuPlans.value = [];
  }
}

function apiUrl(path: string) {
  let base = (functionUrl.value || '/').trim();
  // normalize base (remove trailing slashes)
  base = base.replace(/\/+$/, '');
  // ensure path starts with '/'
  let p = path.startsWith('/') ? path : `/${path}`;
  const baseHasApi = /\/api$/i.test(base);
  const pathHasApi = /^\/api(\/|$)/i.test(p);
  if (baseHasApi && pathHasApi) {
    // avoid double /api
    p = p.replace(/^\/api/i, '');
  }
  const full = base + (baseHasApi ? '' : '/api') + p;
  return full;
}

async function doFetch(method: string, path: string, body?: any) {
  const url = apiUrl(path);
  const headers: any = {
    'Content-Type': 'application/json',
    'x-shop-api-key': shopApiKey.value
  };
  const reqInit: RequestInit = { method, headers };
  if (body) reqInit.body = JSON.stringify(body);

  sentRequest.value = `${method} ${url}\n${JSON.stringify(headers)}\n${body ? JSON.stringify(body, null, 2) : ''}`;

  const start = performance.now();
  try {
    const res = await fetch(url, reqInit);
    const text = await res.text();
    const elapsedMs = Math.round(performance.now() - start);
    response.value = { success: res.ok, status: res.status, statusText: res.statusText, elapsedMs, body: text };
  } catch (err: any) {
    const elapsedMs = Math.round(performance.now() - start);
    response.value = { success: false, status: 0, statusText: String(err?.message || 'Fetch failed'), elapsedMs, body: String(err) };
  }
}

async function resolveLatestSku(basePlan: string): Promise<string> {
  await doFetch('GET', `/shop/plans/${encodeURIComponent(basePlan)}/latest`);
  if (!response.value?.success) throw new Error(`Unknown base plan: ${basePlan}`);
  try {
    const json = JSON.parse(response.value.body);
    return (json && (json.sku || json.Sku)) || basePlan;
  } catch {
    return basePlan;
  }
}

async function lookupLatest() {
  if (!latestBase.value) return;
  loadingLatest.value = true;
  try {
    await doFetch('GET', `/shop/plans/${encodeURIComponent(latestBase.value)}/latest`);
    if (response.value?.success) {
      try {
        const json = JSON.parse(response.value.body);
        latestSku.value = (json && (json.sku || json.Sku)) || latestBase.value;
      } catch {
        latestSku.value = latestBase.value;
      }
    } else {
      latestSku.value = null;
    }
  } catch (e: any) {
    latestSku.value = null;
  } finally {
    loadingLatest.value = false;
  }
}

async function provision() {
  loadingProvision.value = true;
  try {
    if (!expiresAt.value) throw new Error('expiresAt is required for provision');
    const sku = plan.value; // use explicit SKU selected in dropdown
    const body = { sku, subscriptionId: subscriptionId.value, expiresAt: expiresAt.value };
    await doFetch('POST', '/shop/provision', body);
  } finally {
    loadingProvision.value = false;
  }
}

async function upgrade() {
  loadingUpgrade.value = true;
  try {
    if (!upgradeExpiresAt.value) throw new Error('expiresAt is required for upgrade');
    const sku = newPlan.value; // use explicit SKU selected in dropdown
    const body = { sku, expiresAt: upgradeExpiresAt.value };
    await doFetch('PUT', `/shop/upgrade/${encodeURIComponent(subscriptionId.value)}`, body);
  } finally {
    loadingUpgrade.value = false;
  }
}

async function renew() {
  loadingRenew.value = true;
  try {
    if (!renewExpiresAt.value) throw new Error('expiresAt is required for renew');
    const body = { expiresAt: renewExpiresAt.value };
    await doFetch('PUT', `/shop/renew/${encodeURIComponent(subscriptionId.value)}`, body);
  } finally {
    loadingRenew.value = false;
  }
}

async function cancelSub() {
  loadingCancel.value = true;
  try {
    if (!cancelAt.value) throw new Error('cancelAt is required for cancel');
    const body = { cancelAt: cancelAt.value };
    await doFetch('PUT', `/shop/cancel/${encodeURIComponent(subscriptionId.value)}`, body);
  } finally {
    loadingCancel.value = false;
  }
}

async function rotate() {
  loadingRotate.value = true;
  try {
    await doFetch('PUT', `/shop/rotate/${encodeURIComponent(subscriptionId.value)}`);
  } finally {
    loadingRotate.value = false;
  }
}

async function getUsage() {
  loadingUsage.value = true;
  try {
    await doFetch('GET', `/shop/usage/${encodeURIComponent(subscriptionId.value)}`);
  } finally {
    loadingUsage.value = false;
  }
}

function onKeyChanged() {
  try {
    if (shopApiKey.value) localStorage.setItem('shopApiKey', shopApiKey.value);
    else localStorage.removeItem('shopApiKey');
  } catch { /* ignore */ }
}
</script>



